import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AdminAuditTypesTable } from '../-components/AdminAuditTypesTable';
import { CreateAuditTypeDialog } from '../-components/CreateAuditTypeDialog';
import { DeleteAuditTypeDialog } from '../-components/DeleteAuditTypeDialog';
import { DeprecateAuditTypeDialog } from '../-components/DeprecateAuditTypeDialog';
import { adminAuditTypesQueryOptions } from '../-hooks/useAdminAuditTypes';
import { useAdminAuditTypesPage } from '../-hooks/useAdminAuditTypesPage';

const auditTypesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/audit-types/(pages)/')({
  validateSearch: auditTypesSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject hands
  // the route to the router's error boundary, which hides the table's own
  // "Could not load" banner. Matches the other management lists.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminAuditTypesQueryOptions(deps))
      .catch(() => undefined),
  component: AuditTypesPage,
});

function AuditTypesPage() {
  const page = useAdminAuditTypesPage();

  return (
    <PageContainer>
      <AdminAuditTypesTable page={page} />
      <CreateAuditTypeDialog page={page} />
      <DeprecateAuditTypeDialog
        auditType={page.pendingUsage}
        onCancel={page.cancelUsageChange}
        onConfirm={() => void page.confirmUsage()}
        isPending={page.isPending}
      />
      <DeleteAuditTypeDialog
        auditType={page.pendingDelete}
        onCancel={page.cancelDelete}
        onConfirm={() => void page.confirmDelete()}
        isPending={page.isPending}
      />
    </PageContainer>
  );
}
